/**
 * TemplateSelector - Componente para selecionar um template
 * 
 * Permite ao usuário escolher entre os templates disponíveis
 * para configurar um processo.
 */

import React from 'react'
import type { TemplateDefinition, TemplateSelectorProps } from '../types'
import { cn } from '../../../utils/formatter'
import { HiTemplate, HiInformationCircle } from 'react-icons/hi'

const selectClasses = cn(
  'w-full px-3 py-2 text-sm rounded-lg border transition-colors duration-200',
  'bg-white dark:bg-neutral-800',
  'border-gray-300 dark:border-neutral-600',
  'text-gray-900 dark:text-gray-100',
  'placeholder:text-gray-400 dark:placeholder:text-gray-500',
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  'appearance-none cursor-pointer',
  'bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")] bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] pr-10'
)

/**
 * Componente de seleção de template
 */
export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  value,
  onChange,
  templates,
  placeholder = 'Selecione um template...',
  disabled = false,
  className,
}) => {
  // Agrupar templates por categoria
  const templatesByCategory = React.useMemo(() => {
    const grouped = new Map<string, TemplateDefinition[]>()
    templates.forEach(template => {
      const categoria = template.categoria || 'Outros'
      if (!grouped.has(categoria)) {
        grouped.set(categoria, [])
      }
      grouped.get(categoria)!.push(template)
    })
    return grouped
  }, [templates])

  // Template selecionado
  const selectedTemplate = React.useMemo(
    () => templates.find(t => t.id === value),
    [templates, value]
  )

  return (
    <div className={cn('space-y-3', className)}>
      {/* Select */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
          <HiTemplate className="w-4 h-4" />
        </div>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={cn(selectClasses, 'pl-9')}
        >
          <option value="">{placeholder}</option>
          {Array.from(templatesByCategory.entries()).map(([categoria, items]) => (
            <optgroup key={categoria} label={categoria}>
              {items.map(template => (
                <option key={template.id} value={template.id}>
                  {template.nome}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Descrição do template selecionado */}
      {selectedTemplate?.descricao && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <HiInformationCircle className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedTemplate.nome}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-300 mt-0.5">
              {selectedTemplate.descricao}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateSelector
